import { FC } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Form, Button } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updatePassword } from '../../../modules/auth/_requests';

const PasswordForm: FC = () => {
  const { t } = useTranslation('security');

  const schema = Yup.object().shape({
    currentPassword: Yup.string().required(t('currentPassword.required')),
    newPassword: Yup.string()
      .min(8, t('password.min'))
      .max(16, t('password.max'))
      .matches(/[a-zA-Z]/, t('password.alphabetic'))
      .matches(/[0-9]/, t('password.numeric'))
      .required(t('newPassword.required')),
    passwordConfirmation: Yup.string()
      .min(8, t('password.min'))
      .max(16, t('password.max'))
      .matches(/[a-zA-Z]/, t('password.alphabetic'))
      .matches(/[0-9]/, t('password.numeric'))
      .required(t('passwordConfirmation.required')),
  });

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      passwordConfirmation: '',
    },
    validationSchema: schema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        await toast.promise(updatePassword(values), {
          pending: t('message.pending'),
          success: t('message.password.success'),
          error: t('message.password.error'),
        });
      } catch (ex) {
        console.error(ex);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit} className="w-100">
      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">
          {t('currentPassword.label')}
        </Form.Label>
        <Form.Control
          type="password"
          placeholder={t('currentPassword.placeholder')}
          {...formik.getFieldProps('currentPassword')}
          autoComplete="current-password"
          aria-autocomplete="none"
          isInvalid={
            formik.touched.currentPassword &&
            formik.errors.currentPassword !== undefined
          }
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.currentPassword}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">
          {t('newPassword.label')}
        </Form.Label>
        <Form.Control
          type="password"
          placeholder={t('newPassword.placeholder')}
          {...formik.getFieldProps('newPassword')}
          autoComplete="new-password"
          aria-autocomplete="none"
          isInvalid={
            formik.touched.newPassword &&
            formik.errors.newPassword !== undefined
          }
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.newPassword}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label className="text-uppercase">
          {t('passwordConfirmation.label')}
        </Form.Label>
        <Form.Control
          type="password"
          placeholder={t('passwordConfirmation.placeholder')}
          {...formik.getFieldProps('passwordConfirmation')}
          autoComplete="new-password"
          aria-autocomplete="none"
          isInvalid={
            formik.touched.passwordConfirmation &&
            formik.errors.passwordConfirmation !== undefined
          }
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.passwordConfirmation}
        </Form.Control.Feedback>
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
      >
        {formik.isSubmitting ? (
          <span className="indicator-progress">
            {t('button.wait') + ' '}
            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
          </span>
        ) : (
          <span className="indicator-label">{t('button.save')}</span>
        )}
      </Button>
    </Form>
  );
};

export default PasswordForm;
